import { FC } from 'react'
import { ReactComponent as TopicLayersIcon } from 'src/assets/svg/topic-layers.svg'
import { ReactComponent as TopicTagIcon } from 'src/assets/svg/topic-tag.svg'
import {
  TOP_ENTRY_LIMIT,
  TopicEntry,
  TopicsOverviewData,
} from 'src/interfaces/topics.interface'
import styles from './topicsOverviewSection.module.scss'
import { TopicsEmptyState } from '../TopicsEmptyState'
import { TopicRow } from '../TopicRow'
import { SOCIAL_TOPICS_COPY, TopicsCopy } from '../copy'

interface TopicsOverviewSectionProps {
  // Wording for the product the card sits in; Social Listening by default.
  copy?: TopicsCopy
  data?: TopicsOverviewData
  // Fill the host column (capped at the design's 1170px) instead of the fixed
  // 1170px the Social Listening campaigns page needs to sit flush with the
  // Mentions Overview widget. Hosts narrower than 1170px (the review brand
  // page) overflowed their edge without it.
  fluid?: boolean
  isError?: boolean
  isLoading?: boolean
}

const COPY = {
  emptySubtopics: 'No subtopics yet',
  emptyTopics: 'No topics yet',
  title: 'Topics & Subtopics Overview',
  topSubtopics: 'Top Subtopic',
  topTopics: 'Top Topics',
  totalSubtopics: 'Total Subtopics',
  totalTopics: 'Total Topics',
}

const TopEntriesCard: FC<{
  emptyLabel: string
  entries: TopicEntry[]
  title: string
}> = ({ emptyLabel, entries, title }) => {
  const shown = entries.slice(0, TOP_ENTRY_LIMIT)
  // Every bar is read against the leader on show, so the scale comes from the
  // rows actually rendered rather than the full list.
  const max = shown.reduce((acc, entry) => Math.max(acc, entry.mentions), 0)

  return (
    <div className={styles.topCard}>
      <span className={styles.topCardTitle}>{title}</span>
      {shown.length === 0 ? (
        <span className={styles.empty}>{emptyLabel}</span>
      ) : (
        <div className={styles.topCardRows}>
          {shown.map((entry, index) => (
            <TopicRow entry={entry} key={entry.id} max={max} rank={index + 1} />
          ))}
        </div>
      )}
    </div>
  )
}

export const TopicsOverviewSection: FC<TopicsOverviewSectionProps> = ({
  copy = SOCIAL_TOPICS_COPY,
  data,
  fluid = false,
  isError = false,
  isLoading = false,
}) => {
  const sectionClass = `${styles.section} ${fluid ? styles.sectionFluid : ''}`
  // The skeleton is only for an in-flight request. A disabled query (no
  // campaign, no window), a failed one, or a campaign with nothing to show
  // all resolve to an honest empty state rather than a shimmer that never ends.
  const isEmpty =
    !isLoading &&
    (isError || !data || (data.totalTopics === 0 && data.totalSubtopics === 0))

  if (isEmpty) {
    return (
      <section className={sectionClass}>
        <TopicsEmptyState title={COPY.title} />
      </section>
    )
  }

  if (isLoading || !data) {
    return (
      <section className={sectionClass}>
        <div className={styles.header}>
          <h2 className={styles.title}>{COPY.title}</h2>
          <p className={styles.subtitle}>{copy.subtitle}</p>
        </div>
        {/* Same three-column footprint as the loaded state, so the page does
            not reflow when ~800ms of request finally returns. */}
        <div className={styles.body}>
          <div className={styles.totals}>
            <div className={`${styles.totalCard} ${styles.skeletonBlock}`} />
            <div className={`${styles.totalCard} ${styles.skeletonBlock}`} />
          </div>
          <div className={`${styles.topCard} ${styles.skeletonBlock}`} />
          <div className={`${styles.topCard} ${styles.skeletonBlock}`} />
        </div>
      </section>
    )
  }

  return (
    <section className={sectionClass}>
      <div className={styles.header}>
        <h2 className={styles.title}>{COPY.title}</h2>
        <p className={styles.subtitle}>{copy.subtitle}</p>
      </div>

      <div className={styles.body}>
        <div className={styles.totals}>
          <div className={styles.totalCard}>
            <div className={styles.totalHeader}>
              <TopicLayersIcon className={styles.totalIcon} />
              <span className={styles.totalLabel}>{COPY.totalTopics}</span>
            </div>
            <span className={styles.totalValue}>
              {data.totalTopics.toLocaleString()}
            </span>
          </div>
          <div className={styles.totalCard}>
            <div className={styles.totalHeader}>
              <TopicTagIcon className={styles.totalIcon} />
              <span className={styles.totalLabel}>{COPY.totalSubtopics}</span>
            </div>
            <span className={styles.totalValue}>
              {data.totalSubtopics.toLocaleString()}
            </span>
          </div>
        </div>

        <TopEntriesCard
          emptyLabel={COPY.emptyTopics}
          entries={data.topTopics}
          title={COPY.topTopics}
        />
        <TopEntriesCard
          emptyLabel={COPY.emptySubtopics}
          entries={data.topSubtopics}
          title={COPY.topSubtopics}
        />
      </div>

      {/* No summary means no band at all, rather than an empty purple strip. */}
      {!!data.insight && (
        <div className={styles.insight}>
          <svg
            aria-hidden="true"
            className={styles.insightIcon}
            fill="none"
            viewBox="0 0 16 16"
          >
            <path
              d="M8 1.5L9.3 5.4L13.2 6.7L9.3 8L8 11.9L6.7 8L2.8 6.7L6.7 5.4L8 1.5Z"
              fill="currentColor"
            />
            <path
              d="M12.8 10.4L13.4 12.1L15.1 12.7L13.4 13.3L12.8 15L12.2 13.3L10.5 12.7L12.2 12.1L12.8 10.4Z"
              fill="currentColor"
            />
          </svg>
          <span className={styles.insightText}>{data.insight}</span>
        </div>
      )}
    </section>
  )
}
